import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare, Plus, Play, Pause, Trash2, BarChart3,
  Calendar, Users, TrendingUp, Clock, CheckCircle, XCircle, Inbox,
  Facebook, Instagram, Twitter, Linkedin, Cloud, Phone, Mail, Hash, Pencil
} from 'lucide-react';
import { campaignsApi, Campaign, CampaignStats, CreateCampaignRequest, UpdateCampaignRequest, chatflowsApi, Chatflow, Engagement, CampaignEngagementLink } from '@/services/campaignsApi';
import { surveysApi } from '@/services/surveysApi';
import { groupsApi } from '@/services/groupsApi';
import useCommunicationResponses from '@/hooks/useCommunicationResponses';
import { ResponseList } from './ResponseList';
import CampaignResponsesPanel from './CampaignResponsesPanel';
import CampaignMemberSend from './CampaignMemberSend';
import { EngagementSequenceBuilder } from './EngagementSequenceBuilder';
import type { EngagementStep } from './EngagementSequenceBuilder';
import CapabilityGate from '../common/CapabilityGate';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  VariablePicker,
  insertToken,
  namedVariableRejection,
  type NamedVariableRejection,
  type TemplateVariable,
} from '@/components/messaging/templateVariables';

/**
 * A public social post is not addressed to a member, so it can only resolve
 * organization variables (routers/campaigns.py renders it without member data).
 */
const isPublicPostVariable = (v: TemplateVariable) => v.category === 'organization';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

// Social platform configuration
const SOCIAL_PLATFORMS = [
  { code: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
  { code: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-600 to-pink-500' },
  { code: 'twitter', name: 'X (Twitter)', icon: Twitter, color: 'bg-black' },
  { code: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700' },
  { code: 'bluesky', name: 'Bluesky', icon: Cloud, color: 'bg-sky-500' }
];

interface SocialConnection {
  id: string;
  platform_code: string;
  platform_name: string;
  account_name: string;
  connection_status: string;
}
interface Group {
  id: string;
  name: string;
  member_count: number;
}

export const CampaignsPage: React.FC = () => {
  const { t } = useAppTranslation();
  const socialContentRef = useRef<HTMLTextAreaElement | null>(null);
  // TRUS-719: the server's named error when a public post uses member variables.
  const [socialVariableError, setSocialVariableError] = useState<NamedVariableRejection | null>(null);
  const { fmt } = useFormattedDate();
  // Get organization ID from localStorage
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignStats, setCampaignStats] = useState<CampaignStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active' | 'completed' | 'cancelled'>('all');
  const [responsesModalCampaign, setResponsesModalCampaign] = useState<Campaign | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [aiResponsesCampaign, setAiResponsesCampaign] = useState<Campaign | null>(null);
  const [memberSendCampaign, setMemberSendCampaign] = useState<Campaign | null>(null);
  const [showSequenceBuilder, setShowSequenceBuilder] = useState(false);

  // Communication responses for selected campaign
  const {
    responses: campaignResponses,
    loading: responsesLoading,
    error: responsesError,
    unreadCount: responsesUnreadCount,
    refetch: refetchResponses,
    markAsRead: markResponseAsRead,
    updateFollowUp: updateResponseFollowUp
  } = useCommunicationResponses({
    campaignId: responsesModalCampaign?.id ? parseInt(responsesModalCampaign.id) : undefined,
    autoFetch: !!responsesModalCampaign
  });

  const [formData, setFormData] = useState<CreateCampaignRequest>({
    name: '',
    description: '',
    organization_id: 1,
    group_id: '',
    campaign_type: 'outreach',
    engagement_ids: [],
    schedule_config: {},
    channels: ['sms'],
    social_platforms: [],
    social_content: '',
    hashtags: ''
  });

  // Social media connections for the organization
  const [socialConnections, setSocialConnections] = useState<SocialConnection[]>([]);
  const [loadingSocialConnections, setLoadingSocialConnections] = useState(false);

  // Chatflows for chatflow campaign type
  const [availableChatflows, setAvailableChatflows] = useState<Chatflow[]>([]);
  const [selectedChatflowId, setSelectedChatflowId] = useState<string>('');

  // Surveys for survey campaign type
  const [availableSurveys, setAvailableSurveys] = useState<any[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');

  // Engagements for outreach/reminder campaigns
  const [availableEngagements, setAvailableEngagements] = useState<Engagement[]>([]);
  const [selectedEngagementIds, setSelectedEngagementIds] = useState<string[]>([]);

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  // "Use this template" → land here with a pending engagement to attach.
  // TemplateLibraryGallery clones a template, materialises a matching
  // engagement, then stashes the engagement id + suggested campaign name
  // under this key. We auto-open the Create-Campaign form here so the user
  // sees their template appear immediately as a campaign in progress.
  useEffect(() => {
    if (!organizationId) return;
    const raw = sessionStorage.getItem('pendingCampaignFromTemplate');
    if (!raw) return;
    sessionStorage.removeItem('pendingCampaignFromTemplate');
    try {
      const pending = JSON.parse(raw) as {
        engagementId?: string;
        campaignName?: string;
        channel?: string;
      };
      if (!pending.engagementId) return;
      setFormData((prev) => ({
        ...prev,
        name: pending.campaignName ? `${pending.campaignName} — Campaign` : prev.name,
        organization_id: organizationId,
        campaign_type: 'outreach',
        channels: pending.channel === 'email' ? ['email'] : ['sms'],
      }));
      setSelectedEngagementIds([pending.engagementId]);
      setShowCreateForm(true);
    } catch (err) {
      console.warn('pendingCampaignFromTemplate parse failed:', err);
    }
  }, [organizationId]);

  // Load social connections and chatflows when form is opened
  useEffect(() => {
    if ((showCreateForm || showEditForm) && organizationId) {
      // Fire all four in parallel — they're independent and non-blocking
      Promise.all([
        loadSocialConnections(),
        loadAvailableChatflows(),
        loadAvailableSurveys(),
        loadAvailableEngagements(),
      ]);
    }
  }, [showCreateForm, showEditForm, organizationId]);

  const loadAvailableChatflows = async () => {
    if (!organizationId) return;
    try {
      const data = await chatflowsApi.listChatflows({ organization_id: organizationId, is_active: true });
      setAvailableChatflows(Array.isArray(data) ? data : (data as any).chatflows || []);
    } catch (err) {
      console.warn('Failed to load chatflows:', err);
    }
  };

  const loadAvailableSurveys = async () => {
    if (!organizationId) return;
    try {
      // Load from both survey systems and combine
      const combined: any[] = [];

      // 1. Surveys API (/api/surveys)
      try {
        const data = await surveysApi.listSurveys({ organization_id: organizationId });
        const list = Array.isArray(data) ? data : [];
        list.forEach((s: any) => combined.push({ ...s, source: 'surveys' }));
      } catch {}

      // 2. Interactive Content API (/api/interactive-content?content_type=survey)
      try {
        const { apiClient: api } = await import('@/services/apiClient');
        const icRes = await api.get('/api/interactive-content', { params: { content_type: 'survey', status: 'active' } });
        const icData = icRes.data?.items || icRes.data || [];
        if (Array.isArray(icData)) {
          icData.forEach((ic: any) => combined.push({
            id: ic.uuid || String(ic.id),
            name: `${ic.name} (Surveys & Polls)`,
            question_count: ic.question_count || ic.questions?.length || 0,
            source: 'interactive_content',
          }));
        }
      } catch {}

      setAvailableSurveys(combined);
    } catch (err) {
      console.warn('Failed to load surveys:', err);
    }
  };

  const loadAvailableEngagements = async () => {
    if (!organizationId) return;
    try {
      const data = await campaignsApi.listEngagements({ organization_id: organizationId });
      setAvailableEngagements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Failed to load engagements:', err);
    }
  };

  const loadSocialConnections = async () => {
    if (!organizationId) return;

    setLoadingSocialConnections(true);
    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/connections?organization_id=${organizationId}`);

      if (response.ok) {
        const connections = await response.json();
        setSocialConnections(connections.filter((c: SocialConnection) => c.connection_status === 'active'));
      }
    } catch (error) {
      console.error('Failed to load social connections:', error);
    } finally {
      setLoadingSocialConnections(false);
    }
  };

  const loadData = async () => {
    try {
      // Only show full loading spinner on initial load
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      const [campaignsData, groupsData] = await Promise.all([
        campaignsApi.listCampaigns({
          status_filter: statusFilter === 'all' ? undefined : statusFilter
        }),
        groupsApi.listGroups()
      ]);
      setCampaigns(campaignsData);
      setGroups(groupsData);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const payload = { ...formData };
      // For chatflow campaigns, attach the selected chatflow_id
      if (payload.campaign_type === 'chatflow' && selectedChatflowId) {
        payload.chatflow_id = selectedChatflowId;
        // Chatflow defines its own channels, set a default
        payload.channels = ['sms'];
      }
      // For survey campaigns, pass selected survey as an engagement
      if (payload.campaign_type === 'survey' && selectedSurveyId) {
        payload.survey_id = selectedSurveyId;
      }
      // For outreach/reminder campaigns, pass selected engagement IDs
      if (['outreach', 'reminder'].includes(payload.campaign_type) && selectedEngagementIds.length > 0) {
        payload.engagement_ids = selectedEngagementIds;
      }
      const created = await campaignsApi.createCampaign(payload);
      // If engagements were selected for outreach/reminder, link them to the campaign
      if (['outreach', 'reminder'].includes(payload.campaign_type) && selectedEngagementIds.length > 0 && created?.id) {
        const links: CampaignEngagementLink[] = selectedEngagementIds.map((eid, idx) => ({
          engagement_id: eid,
          engagement_order: idx + 1,
        }));
        await campaignsApi.setCampaignEngagements(created.id, links);
      }
      setShowCreateForm(false);
      resetForm();
      setSelectedChatflowId('');
      setSelectedSurveyId('');
      setSelectedEngagementIds([]);
      loadData();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      const rejection = namedVariableRejection(error);
      if (rejection) {
        setSocialVariableError(rejection);
        return;
      }
      alert('Failed to create campaign. Please try again.');
    }
  };

  const handleEditCampaign = async () => {
    if (!editingCampaign) return;
    try {
      const updates: UpdateCampaignRequest = {
        name: formData.name,
        description: formData.description,
        campaign_type: formData.campaign_type,
        channels: formData.channels,
        social_platforms: formData.social_platforms,
        social_content: formData.social_content,
        hashtags: formData.hashtags,
        start_date: formData.start_date,
        end_date: formData.end_date,
        group_id: formData.group_id,
      };
      await campaignsApi.updateCampaign(editingCampaign.id, updates);
      setShowEditForm(false);
      setEditingCampaign(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to update campaign:', error);
      const rejection = namedVariableRejection(error);
      if (rejection) {
        setSocialVariableError(rejection);
        return;
      }
      alert('Failed to update campaign. Please try again.');
    }
  };

  const openEditForm = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      organization_id: campaign.organization_id,
      group_id: campaign.group_id || '',
      campaign_type: campaign.campaign_type,
      engagement_ids: [],
      schedule_config: {},
      channels: campaign.channels || ['sms'],
      social_platforms: campaign.social_platforms || [],
      social_content: campaign.social_content || '',
      hashtags: campaign.hashtags || '',
      start_date: campaign.start_date,
      end_date: campaign.end_date,
    });
    setShowEditForm(true);
  };

  const handleLaunchCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to launch this campaign? Messages will be sent to all group members.')) return;

    try {
      const result = await campaignsApi.launchCampaign(campaignId);
      alert(`Campaign launched! ${result.message}`);
      loadData();
    } catch (error: any) {
      console.error('Failed to launch campaign:', error);
      const msg = error?.message || error?.detail || 'Unknown error';
      alert(`Failed to launch campaign: ${msg}`);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await campaignsApi.deleteCampaign(campaignId);
      loadData();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    }
  };

  const handleViewStats = async (campaign: Campaign) => {
    try {
      const stats = await campaignsApi.getCampaignStats(campaign.id);
      setSelectedCampaign(campaign);
      setCampaignStats(stats);
    } catch (error) {
      console.error('Failed to load campaign stats:', error);
      alert('Failed to load campaign statistics.');
    }
  };

  const resetForm = () => {
    setSocialVariableError(null);
    setFormData({
      name: '',
      description: '',
      organization_id: 1,
      group_id: '',
      campaign_type: 'outreach',
      engagement_ids: [],
      schedule_config: {},
      channels: ['sms'],
      social_platforms: [],
      social_content: '',
      hashtags: ''
    });
  };

  // Toggle channel selection
  const toggleChannel = (channel: string) => {
    const channels = formData.channels || [];
    if (channels.includes(channel)) {
      setFormData({ ...formData, channels: channels.filter(c => c !== channel) });
    } else {
      setFormData({ ...formData, channels: [...channels, channel] });
    }
  };

  // Toggle social platform selection
  const toggleSocialPlatform = (platformCode: string) => {
    const platforms = formData.social_platforms || [];
    if (platforms.includes(platformCode)) {
      setFormData({ ...formData, social_platforms: platforms.filter(p => p !== platformCode) });
    } else {
      setFormData({ ...formData, social_platforms: [...platforms, platformCode] });
    }
  };

  // Check if a social platform is connected
  const isPlatformConnected = (platformCode: string) => {
    return socialConnections.some(c => c.platform_code === platformCode);
  };

  // Get channel icons for display
  const getChannelIcons = (campaign: Campaign) => {
    const icons = [];
    const channels = campaign.channels || ['sms'];

    if (channels.includes('sms')) icons.push({ icon: Phone, color: 'text-green-600', label: 'SMS' });
    if (channels.includes('whatsapp')) icons.push({ icon: MessageSquare, color: 'text-green-600', label: 'WhatsApp' });
    if (channels.includes('email')) icons.push({ icon: Mail, color: 'text-blue-600', label: 'Email' });
    if (channels.includes('voice')) icons.push({ icon: Phone, color: 'text-purple-600', label: 'Voice' });
    if (channels.includes('rcs')) icons.push({ icon: Phone, color: 'text-indigo-600', label: 'RCS' });

    const socialPlatforms = campaign.social_platforms || [];
    socialPlatforms.forEach(p => {
      const platform = SOCIAL_PLATFORMS.find(sp => sp.code === p);
      if (platform) {
        icons.push({ icon: platform.icon, color: 'text-gray-700', label: platform.name });
      }
    });

    return icons;
  };

  const getStatusBadge = (status: Campaign['status']) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      scheduled: 'bg-yellow-100 text-yellow-700',
      paused: 'bg-orange-100 text-orange-700',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };

    const icons: Record<string, React.ComponentType<{ className?: string }>> = {
      draft: Clock,
      active: Play,
      scheduled: Calendar,
      paused: Pause,
      completed: CheckCircle,
      cancelled: XCircle
    };

    const Icon = icons[status] || Clock;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getCampaignTypeIcon = (type: Campaign['campaign_type']) => {
    switch (type) {
      case 'outreach': return <MessageSquare className="w-4 h-4" />;
      case 'reminder': return <Clock className="w-4 h-4" />;
      case 'survey': return <BarChart3 className="w-4 h-4" />;
      case 'chatflow': return <MessageSquare className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="communications"
      upgradeMessage="Communications and Campaigns is not enabled for your organization. Contact your administrator to enable this feature."
    >
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        {/* Toolbar */}
        <div className="bg-surface border-b border-v2border px-6 py-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={() => {
                // Send to the Template Library tab (UUHP demo Flow 4).
                // Falls back to the create form if the host page can't navigate.
                try {
                  const w: any = window as any;
                  if (typeof w.__navigateWithHistory === 'function') {
                    w.__navigateWithHistory('template-library');
                  } else {
                    window.location.hash = '#template-library';
                  }
                } catch {
                  setShowCreateForm(true);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 border border-v2border bg-surface text-ink rounded-lg hover:bg-paper-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('communications.new_from_template', 'New from Template')}
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('communications.create_campaign', 'Create Campaign')}
            </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('communications.status', 'Status:')}</span>
          {(['all', 'draft', 'active', 'completed', 'cancelled'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                statusFilter === status
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">{t('communications.loading_campaigns', 'Loading campaigns...')}</p>
            </div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {statusFilter === 'all' ? 'No campaigns yet' : `No ${statusFilter} campaigns`}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md">
              {t('communications.create_your_first_campaign_to', 'Create your first campaign to send scheduled, multi-step communications to member groups')}
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('communications.create_your_first_campaign', 'Create Your First Campaign')}
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden w-full">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Group</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Members</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Dates</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {campaigns.map((campaign) => (
              <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="text-purple-600">{getCampaignTypeIcon(campaign.campaign_type)}</div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                        {campaign.name}
                        {campaign.status === 'draft' && campaign.engagement_count === 0 && campaign.campaign_type !== 'chatflow' && (
                          <span title="No engagements attached — add engagements before launching" className="text-amber-500">
                            <Clock className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      {campaign.description && <div className="text-xs text-gray-500 truncate max-w-xs">{campaign.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">{campaign.campaign_type}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{campaign.group_name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{campaign.total_members}</td>
                <td className="px-4 py-3">{getStatusBadge(campaign.status)}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {campaign.start_date ? fmt.short(campaign.start_date) : '—'}
                  {campaign.end_date && ` → ${fmt.short(campaign.end_date)}`}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {campaign.status === 'draft' && (
                      <>
                        <button onClick={() => handleLaunchCampaign(campaign.id)} className="text-xs px-2 py-1 rounded text-green-600 hover:bg-green-50 flex items-center gap-1">
                          <Play className="w-3 h-3" /> Launch
                        </button>
                        <button onClick={() => openEditForm(campaign)} className="text-xs px-2 py-1 rounded text-yellow-600 hover:bg-yellow-50 flex items-center gap-1">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      </>
                    )}
                    {(campaign.status === 'active' || campaign.status === 'completed') && (
                      <>
                        <button onClick={() => handleViewStats(campaign)} className="text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50 flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> Stats
                        </button>
                        <button onClick={() => setAiResponsesCampaign(campaign)} className="text-xs px-2 py-1 rounded text-purple-600 hover:bg-purple-50 flex items-center gap-1">
                          <Inbox className="w-3 h-3" /> AI
                        </button>
                        <button onClick={() => setMemberSendCampaign(campaign)} className="text-xs px-2 py-1 rounded text-teal-600 hover:bg-teal-50 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Send
                        </button>
                      </>
                    )}
                    <button onClick={() => handleDeleteCampaign(campaign.id)} className="text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('communications.create_new_campaign', 'Create New Campaign')}</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.campaign_name', 'Campaign Name *')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Fall Vaccination Reminder"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.description', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('communications.describe_the_campaigns_purpose_and', 'Describe the campaign\'s purpose and goals')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.target_group', 'Target Group *')}
                </label>
                <select
                  value={formData.group_id}
                  onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">{t('communications.select_a_group', 'Select a group...')}</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.member_count} members)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.campaign_type', 'Campaign Type *')}
                </label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => {
                    const newType = e.target.value as any;
                    setFormData({ ...formData, campaign_type: newType });
                    if (newType === 'chatflow') setSelectedChatflowId('');
                    if (newType !== 'survey') setSelectedSurveyId('');
                    if (!['outreach', 'reminder'].includes(newType)) setSelectedEngagementIds([]);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="outreach">{t('communications.outreach', 'Outreach')}</option>
                  <option value="reminder">{t('communications.reminder', 'Reminder')}</option>
                  <option value="survey">{t('communications.survey', 'Survey')}</option>
                  <option value="chatflow">{t('communications.chatflow', 'Chatflow')}</option>
                </select>
              </div>

              {/* Chatflow Selection - only for chatflow campaigns */}
              {formData.campaign_type === 'chatflow' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.select_chatflow', 'Select Chatflow *')}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    The chatflow defines the conversation steps and which channels to use for each member
                  </p>
                  {availableChatflows.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No active chatflows available. Create and activate a chatflow first from the Chatflows page.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedChatflowId}
                      onChange={(e) => setSelectedChatflowId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Select a chatflow --</option>
                      {availableChatflows.map(cf => (
                        <option key={cf.id} value={cf.id}>
                          {cf.name} ({cf.node_count || 0} nodes)
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedChatflowId && (
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        When launched, a chatflow session starts for each member. Channels are defined by chatflow nodes.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Survey Selection - only for survey campaigns */}
              {formData.campaign_type === 'survey' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.select_survey', 'Select Survey *')}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose a survey to send to the campaign audience
                  </p>
                  {availableSurveys.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No active surveys available. Create a survey first from the Surveys page.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedSurveyId}
                      onChange={(e) => setSelectedSurveyId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Select a survey --</option>
                      {availableSurveys.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name || s.title} ({s.question_count || 0} questions)
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedSurveyId && (
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        When launched, a survey link will be sent to each member via the selected channels.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Communication Channels Section - only for non-chatflow campaigns */}
              {!['chatflow', 'survey'].includes(formData.campaign_type) && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('communications.channels', 'Communication Channels *')}
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select one or more channels to reach your audience
                </p>

                {/* Traditional Channels */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Direct Channels</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => toggleChannel('sms')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('sms')
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      SMS
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('email')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('email')
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('whatsapp')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('whatsapp')
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('voice')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('voice')
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Voice
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('rcs')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('rcs')
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      RCS
                    </button>
                  </div>
                </div>

                {/* Attach Engagements - for outreach/reminder campaigns */}
                {['outreach', 'reminder'].includes(formData.campaign_type) && availableEngagements.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Attach Engagements</p>
                  <p className="text-xs text-gray-500 mb-2">
                    Optionally attach existing engagement templates to this campaign
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                    {availableEngagements.map(eng => (
                      <label
                        key={eng.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          selectedEngagementIds.includes(eng.id) ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEngagementIds.includes(eng.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEngagementIds([...selectedEngagementIds, eng.id]);
                            } else {
                              setSelectedEngagementIds(selectedEngagementIds.filter(id => id !== eng.id));
                            }
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {eng.engagement_type === 'sms' && <Phone className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                          {eng.engagement_type === 'email' && <Mail className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                          {eng.engagement_type === 'survey' && <BarChart3 className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />}
                          {eng.engagement_type === 'chatflow' && <MessageSquare className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />}
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{eng.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{eng.engagement_type}</span>
                        </div>
                        {eng.content_template && (
                          <span className="text-xs text-gray-400 truncate max-w-[120px]">{eng.content_template}</span>
                        )}
                      </label>
                    ))}
                  </div>
                  {selectedEngagementIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedEngagementIds.map(eid => {
                        const eng = availableEngagements.find(e => e.id === eid);
                        return eng ? (
                          <span
                            key={eid}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs"
                          >
                            {eng.name}
                            <button
                              type="button"
                              onClick={() => setSelectedEngagementIds(selectedEngagementIds.filter(id => id !== eid))}
                              className="hover:text-purple-900 dark:hover:text-purple-100"
                            >
                              <XCircle className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* Social Media Channels */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Social Media Platforms</p>
                  {loadingSocialConnections ? (
                    <div className="text-sm text-gray-500">Loading connected platforms...</div>
                  ) : socialConnections.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No social media platforms connected.
                        <a href="/settings" className="underline ml-1">Connect platforms in Settings</a>
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {SOCIAL_PLATFORMS.map((platform) => {
                        const isConnected = isPlatformConnected(platform.code);
                        const isSelected = (formData.social_platforms || []).includes(platform.code);
                        const Icon = platform.icon;

                        return (
                          <button
                            key={platform.code}
                            type="button"
                            onClick={() => isConnected && toggleSocialPlatform(platform.code)}
                            disabled={!isConnected}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                              !isConnected
                                ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                                : isSelected
                                ? 'bg-gray-100 border-gray-700 text-gray-900'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                            title={isConnected ? platform.name : `${platform.name} not connected`}
                          >
                            <Icon className="w-4 h-4" />
                            {platform.name}
                            {isConnected && (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Social Media Content Section - Show only if social platforms are selected */}
              {(formData.social_platforms || []).length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.social_content', 'Social Media Content')}
                  </label>
                  <textarea
                    ref={socialContentRef}
                    value={formData.social_content || ''}
                    onChange={(e) => {
                      setSocialVariableError(null);
                      setFormData({ ...formData, social_content: e.target.value });
                    }}
                    aria-invalid={socialVariableError ? true : undefined}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('templateVariables.social.placeholder', 'Write your social media post.')}
                    maxLength={280}
                  />
                  {socialVariableError && (
                    <div
                      role="alert"
                      className="mt-1 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
                      data-testid="social-variable-error"
                    >
                      <p className="font-medium">
                        {socialVariableError.message ||
                          t('templateVariables.social.memberVariables', 'A public post cannot use member variables.')}
                      </p>
                      {socialVariableError.keys.length > 0 && (
                        <p className="mt-0.5">
                          {t('templateVariables.social.removeThese', 'Remove: {{variables}}', {
                            variables: socialVariableError.keys.map((k) => `{{${k}}}`).join(', '),
                          })}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-1">
                    <VariablePicker
                      appliesTo="messages"
                      filter={isPublicPostVariable}
                      label={t('templateVariables.social.insert', 'Insert organization variable')}
                      onInsert={(key) =>
                        setFormData((prev) => ({
                          ...prev,
                          social_content: insertToken(socialContentRef.current, prev.social_content || '', key),
                        }))
                      }
                    />
                    <p className="text-xs text-gray-500">
                      {(formData.social_content || '').length}/280 characters
                    </p>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Hash className="w-4 h-4 inline mr-1" />
                      {t('communications.hashtags', 'Hashtags')}
                    </label>
                    <input
                      type="text"
                      value={formData.hashtags || ''}
                      onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="#HealthAwareness #CommunityHealth"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate hashtags with spaces
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('communications.start_date', 'Start Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('communications.end_date', 'End Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setShowSequenceBuilder(true)}
                className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                {t('communications.build_sequence', 'Build Engagement Sequence')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('communications.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleCreateCampaign}
                  disabled={!formData.name || !formData.group_id || (formData.campaign_type === 'chatflow' ? !selectedChatflowId : formData.campaign_type === 'survey' ? !selectedSurveyId : ((formData.channels || []).length === 0 && (formData.social_platforms || []).length === 0))}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('communications.create_campaign_1', 'Create Campaign')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditForm && editingCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('communications.edit_campaign', 'Edit Campaign')}</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.campaign_name', 'Campaign Name *')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Fall Vaccination Reminder"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.description', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('communications.describe_the_campaigns_purpose_and', 'Describe the campaign\'s purpose and goals')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.target_group', 'Target Group *')}
                </label>
                <select
                  value={formData.group_id}
                  onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">{t('communications.select_a_group', 'Select a group...')}</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.member_count} members)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.campaign_type', 'Campaign Type *')}
                </label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => {
                    const newType = e.target.value as any;
                    setFormData({ ...formData, campaign_type: newType });
                    if (newType === 'chatflow') setSelectedChatflowId('');
                    if (newType !== 'survey') setSelectedSurveyId('');
                    if (!['outreach', 'reminder'].includes(newType)) setSelectedEngagementIds([]);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="outreach">{t('communications.outreach', 'Outreach')}</option>
                  <option value="reminder">{t('communications.reminder', 'Reminder')}</option>
                  <option value="survey">{t('communications.survey', 'Survey')}</option>
                  <option value="chatflow">{t('communications.chatflow', 'Chatflow')}</option>
                </select>
              </div>

              {/* Chatflow Selection - only for chatflow campaigns */}
              {formData.campaign_type === 'chatflow' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.select_chatflow', 'Select Chatflow *')}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    The chatflow defines the conversation steps and which channels to use for each member
                  </p>
                  {availableChatflows.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No active chatflows available. Create and activate a chatflow first from the Chatflows page.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedChatflowId}
                      onChange={(e) => setSelectedChatflowId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Select a chatflow --</option>
                      {availableChatflows.map(cf => (
                        <option key={cf.id} value={cf.id}>
                          {cf.name} ({cf.node_count || 0} nodes)
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedChatflowId && (
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        When launched, a chatflow session starts for each member. Channels are defined by chatflow nodes.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Survey Selection - only for survey campaigns */}
              {formData.campaign_type === 'survey' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.select_survey', 'Select Survey *')}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose a survey to send to the campaign audience
                  </p>
                  {availableSurveys.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No active surveys available. Create a survey first from the Surveys page.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedSurveyId}
                      onChange={(e) => setSelectedSurveyId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Select a survey --</option>
                      {availableSurveys.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name || s.title} ({s.question_count || 0} questions)
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedSurveyId && (
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        When launched, a survey link will be sent to each member via the selected channels.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Communication Channels Section - only for non-chatflow campaigns */}
              {!['chatflow', 'survey'].includes(formData.campaign_type) && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('communications.channels', 'Communication Channels *')}
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select one or more channels to reach your audience
                </p>

                {/* Traditional Channels */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Direct Channels</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => toggleChannel('sms')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('sms')
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      SMS
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('email')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('email')
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('whatsapp')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('whatsapp')
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('voice')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('voice')
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Voice
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleChannel('rcs')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        (formData.channels || []).includes('rcs')
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      RCS
                    </button>
                  </div>
                </div>

                {/* Attach Engagements - for outreach/reminder campaigns */}
                {['outreach', 'reminder'].includes(formData.campaign_type) && availableEngagements.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Attach Engagements</p>
                  <p className="text-xs text-gray-500 mb-2">
                    Optionally attach existing engagement templates to this campaign
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                    {availableEngagements.map(eng => (
                      <label
                        key={eng.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          selectedEngagementIds.includes(eng.id) ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEngagementIds.includes(eng.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEngagementIds([...selectedEngagementIds, eng.id]);
                            } else {
                              setSelectedEngagementIds(selectedEngagementIds.filter(id => id !== eng.id));
                            }
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {eng.engagement_type === 'sms' && <Phone className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                          {eng.engagement_type === 'email' && <Mail className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                          {eng.engagement_type === 'survey' && <BarChart3 className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />}
                          {eng.engagement_type === 'chatflow' && <MessageSquare className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />}
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{eng.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{eng.engagement_type}</span>
                        </div>
                        {eng.content_template && (
                          <span className="text-xs text-gray-400 truncate max-w-[120px]">{eng.content_template}</span>
                        )}
                      </label>
                    ))}
                  </div>
                  {selectedEngagementIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedEngagementIds.map(eid => {
                        const eng = availableEngagements.find(e => e.id === eid);
                        return eng ? (
                          <span
                            key={eid}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs"
                          >
                            {eng.name}
                            <button
                              type="button"
                              onClick={() => setSelectedEngagementIds(selectedEngagementIds.filter(id => id !== eid))}
                              className="hover:text-purple-900 dark:hover:text-purple-100"
                            >
                              <XCircle className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* Social Media Channels */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Social Media Platforms</p>
                  {loadingSocialConnections ? (
                    <div className="text-sm text-gray-500">Loading connected platforms...</div>
                  ) : socialConnections.length === 0 ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No social media platforms connected.
                        <a href="/settings" className="underline ml-1">Connect platforms in Settings</a>
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {SOCIAL_PLATFORMS.map((platform) => {
                        const isConnected = isPlatformConnected(platform.code);
                        const isSelected = (formData.social_platforms || []).includes(platform.code);
                        const Icon = platform.icon;

                        return (
                          <button
                            key={platform.code}
                            type="button"
                            onClick={() => isConnected && toggleSocialPlatform(platform.code)}
                            disabled={!isConnected}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                              !isConnected
                                ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                                : isSelected
                                ? 'bg-gray-100 border-gray-700 text-gray-900'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                            title={isConnected ? platform.name : `${platform.name} not connected`}
                          >
                            <Icon className="w-4 h-4" />
                            {platform.name}
                            {isConnected && (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Social Media Content Section - Show only if social platforms are selected */}
              {(formData.social_platforms || []).length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('communications.social_content', 'Social Media Content')}
                  </label>
                  <textarea
                    ref={socialContentRef}
                    value={formData.social_content || ''}
                    onChange={(e) => {
                      setSocialVariableError(null);
                      setFormData({ ...formData, social_content: e.target.value });
                    }}
                    aria-invalid={socialVariableError ? true : undefined}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('templateVariables.social.placeholder', 'Write your social media post.')}
                    maxLength={280}
                  />
                  {socialVariableError && (
                    <div
                      role="alert"
                      className="mt-1 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
                      data-testid="social-variable-error"
                    >
                      <p className="font-medium">
                        {socialVariableError.message ||
                          t('templateVariables.social.memberVariables', 'A public post cannot use member variables.')}
                      </p>
                      {socialVariableError.keys.length > 0 && (
                        <p className="mt-0.5">
                          {t('templateVariables.social.removeThese', 'Remove: {{variables}}', {
                            variables: socialVariableError.keys.map((k) => `{{${k}}}`).join(', '),
                          })}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-1">
                    <VariablePicker
                      appliesTo="messages"
                      filter={isPublicPostVariable}
                      label={t('templateVariables.social.insert', 'Insert organization variable')}
                      onInsert={(key) =>
                        setFormData((prev) => ({
                          ...prev,
                          social_content: insertToken(socialContentRef.current, prev.social_content || '', key),
                        }))
                      }
                    />
                    <p className="text-xs text-gray-500">
                      {(formData.social_content || '').length}/280 characters
                    </p>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Hash className="w-4 h-4 inline mr-1" />
                      {t('communications.hashtags', 'Hashtags')}
                    </label>
                    <input
                      type="text"
                      value={formData.hashtags || ''}
                      onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="#HealthAwareness #CommunityHealth"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate hashtags with spaces
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('communications.start_date', 'Start Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('communications.end_date', 'End Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditForm(false);
                  setEditingCampaign(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('communications.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleEditCampaign}
                disabled={!formData.name || !formData.group_id || ((formData.channels || []).length === 0 && (formData.social_platforms || []).length === 0)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('communications.save_changes', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Stats Modal */}
      {selectedCampaign && campaignStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedCampaign.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('communications.campaign_performance', 'Campaign Performance')}</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-blue-600 mb-2">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{campaignStats.total_members}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{t('communications.total_members', 'Total Members')}</div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-green-600 mb-2">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{campaignStats.messages_delivered}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{t('communications.delivered', 'Delivered')}</div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-purple-600 mb-2">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{campaignStats.response_rate.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{t('communications.response_rate', 'Response Rate')}</div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-red-600 mb-2">
                    <XCircle className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{campaignStats.messages_failed}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{t('communications.failed', 'Failed')}</div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{t('communications.messages_sent', 'Messages Sent:')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{campaignStats.messages_sent}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{t('communications.responses_received', 'Responses Received:')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{campaignStats.responses_received}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{t('communications.completion_rate', 'Completion Rate:')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{campaignStats.completion_rate.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{t('communications.total_cost', 'Total Cost:')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">${campaignStats.total_cost_dollars.toFixed(2)}</span>
                  </div>
                  {campaignStats.avg_response_time_minutes && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-300">{t('communications.avg_response_time', 'Avg Response Time:')}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{campaignStats.avg_response_time_minutes.toFixed(0)} min</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={() => {
                  setSelectedCampaign(null);
                  setCampaignStats(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('communications.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Responses Modal (legacy) */}
      {responsesModalCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{responsesModalCampaign.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('communications.member_responses', 'Member Responses')}</p>
            </div>

            <div className="p-6">
              <ResponseList
                responses={campaignResponses}
                loading={responsesLoading}
                error={responsesError}
                unreadCount={responsesUnreadCount}
                onMarkAsRead={markResponseAsRead}
                onUpdateFollowUp={updateResponseFollowUp}
                onRefresh={refetchResponses}
                showMemberInfo={true}
                emptyMessage="No responses yet for this campaign. Responses will appear here when members reply to SMS, email, or voice communications."
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={() => setResponsesModalCampaign(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('communications.close_1', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Responses Panel */}
      {aiResponsesCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <CampaignResponsesPanel
              campaignId={aiResponsesCampaign.id}
              onClose={() => setAiResponsesCampaign(null)}
            />
          </div>
        </div>
      )}

      {/* Individual Member Send Modal */}
      {memberSendCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <CampaignMemberSend
              campaignId={memberSendCampaign.id}
              onClose={() => setMemberSendCampaign(null)}
            />
          </div>
        </div>
      )}

      {/* Engagement Sequence Builder Modal */}
      {showSequenceBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[95vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('communications.engagement_sequence', 'Engagement Sequence')}
              </h2>
              <button
                onClick={() => setShowSequenceBuilder(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            <EngagementSequenceBuilder
              organizationId={organizationId || 1}
              availableChannels={formData.channels || ['sms']}
              onSave={async (steps: EngagementStep[]) => {
                try {
                  // Create engagements for steps that don't already have an engagement_id
                  const createdLinks: CampaignEngagementLink[] = [];
                  for (let i = 0; i < steps.length; i++) {
                    const step = steps[i];
                    let engagementId = step.engagement_id;
                    if (!engagementId) {
                      // Create a new engagement from the step
                      const created = await campaignsApi.createEngagement({
                        name: step.name || `Step ${i + 1}`,
                        organization_id: organizationId || 1,
                        engagement_type: step.engagement_type || 'sms',
                        content_template: step.content_template || '',
                        survey_id: (step as any).survey_id,
                        chatflow_id: (step as any).chatflow_id,
                        wait_for_response: step.wait_for_response ?? false,
                        timeout_hours: step.timeout_hours ?? 24,
                      });
                      engagementId = created.id;
                    }
                    createdLinks.push({
                      engagement_id: engagementId,
                      engagement_order: i + 1,
                      delay_hours: step.delay_hours ?? 0,
                      condition_type: step.condition_type,
                      condition_config: step.condition_config,
                      is_ab_test: step.is_ab_test,
                      is_parallel: step.is_parallel,
                      timeout_hours: step.timeout_hours,
                    });
                  }
                  // If there's a selected campaign to link to, set the engagements
                  if (selectedCampaign?.id) {
                    await campaignsApi.setCampaignEngagements(selectedCampaign.id, createdLinks);
                  }
                  // Refresh engagements list
                  loadAvailableEngagements();
                  setShowSequenceBuilder(false);
                } catch (error) {
                  console.error('Failed to save engagement sequence:', error);
                  alert('Failed to save engagement sequence. Please try again.');
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
    </CapabilityGate>
  );
};
